import { useEffect, useRef, useState } from "react";
import Organization from "../../models/organization";
import DropdownOption from "../../components/common/DropdownOption";
import organizationService from "../../services/organizationService";
import BoxContent from "../../components/common/BoxContent";
import SignupInvitation from "../../models/signupInvitation";
import { useNavigate } from "react-router-dom";
import fileService from "../../services/fileService";
import Select from "react-select";
import userService from "../../services/userService";

const SignupInvitationsInvite: React.FC = () => {
  const [signupInvitations, setSignupInvitations] = useState<
    SignupInvitation[]
  >([]);

  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const [organizationOptions, setOrganizationOptions] = useState<
    DropdownOption<Organization>[]
  >([]);

  const fileUploadRef = useRef<HTMLInputElement>(null);

  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const init = async () => {
      const { data: organizations } =
        await organizationService.getOrganizations();

      setOrganizations(organizations);

      const organizationOptions: DropdownOption<Organization>[] =
        organizations.map((o) => {
          return { value: o, label: o.organizationName };
        });

      setOrganizationOptions(organizationOptions);
    };

    init();
  }, []);

  const onEmailChanged = (event: any, signupInvitation: SignupInvitation) => {
    const newValue = event.target.value;

    signupInvitation.email = newValue;

    updateInvitations();
  };

  const onFirstNameChanged = (
    event: any,
    signupInvitation: SignupInvitation
  ) => {
    const newValue = event.target.value;

    signupInvitation.firstName = newValue;

    updateInvitations();
  };

  const onLastNameChanged = (
    event: any,
    signupInvitation: SignupInvitation
  ) => {
    const newValue = event.target.value;

    signupInvitation.lastName = newValue;

    updateInvitations();
  };

  const onAddInvitation = () => {
    signupInvitations.push(new SignupInvitation());

    updateInvitations();
  };

  const updateInvitations = () => {
    const newSignupInvitations = [...signupInvitations];
    setSignupInvitations(newSignupInvitations);
  };

  const onRemoveInvitation = (signupInvitation: SignupInvitation) => {
    const newSignupInvitations = signupInvitations.filter(
      (si) => si !== signupInvitation
    );
    setSignupInvitations(newSignupInvitations);
  };

  const onCancel = () => {
    navigateAway();
  };

  const navigateAway = () => {
    navigate("/admin/invitations");
  };

  const onDownloadTemplate = () => {
    const fileName = "invitations.csv";
    const fileHeader = "Organization, Email, First Name, Last Name";

    const fileContent =
      fileHeader +
      "\r\n" +
      organizations.map((o) => o.organizationName).join(",,,\r\n");

    fileService.exportFile(fileContent, fileName);
  };

  const onFileUpload = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e: any) {
        const fileContent = e.target.result;
        onImportInvitations(fileContent);

        // clear selected file so we can reimport the same file again
        if (fileUploadRef.current) {
          fileUploadRef.current.value = "";
        }
      };
      reader.readAsText(file);
    }
  };

  const onImportInvitations = (text: string) => {
    clearError();

    //const fileHeader = "Organization, Email, First Name, Last Name";

    const importedInvitations: SignupInvitation[] = [];

    const skipHeader = true;

    const lines = text.split("\r\n");
    for (
      let lineIndex = skipHeader ? 1 : 0;
      lineIndex < lines.length;
      lineIndex++
    ) {
      const line = lines[lineIndex];

      if (!line) {
        continue;
      }

      const lineData = line.split(",");

      const organizationName = lineData[0];
      const email = lineData[1];
      const firstName = lineData[2];
      const lastName = lineData[3];

      const importedInvitation = new SignupInvitation();
      importedInvitation.email = email;
      importedInvitation.firstName = firstName;
      importedInvitation.lastName = lastName;

      const organization = organizations.find(
        (o) => o.organizationName === organizationName
      );

      if (organization) {
        importedInvitation.organization = organization;
        importedInvitation.idOrganization = organization.idOrganization;
      }

      importedInvitations.push(importedInvitation);
    }

    setSignupInvitations(importedInvitations);
  };

  const getSelectedOrganization = (signupInvitation: SignupInvitation) => {
    if (!signupInvitation.organization) {
      return null;
    }

    const selectedOrganization = organizationOptions.find(
      (opt) =>
        opt.value?.idOrganization ===
        signupInvitation.organization?.idOrganization
    );
    return selectedOrganization;
  };

  const setSelectedOrganization = (
    selectedOrganizationOption: DropdownOption<Organization>,
    signupInvitation: SignupInvitation
  ) => {
    if (!selectedOrganizationOption.value) {
      return;
    }
    signupInvitation.organization = selectedOrganizationOption.value;
    signupInvitation.idOrganization =
      selectedOrganizationOption.value.idOrganization;

    updateInvitations();
  };

  const onInviteUsers = async () => {
    console.log("signupInvitations", signupInvitations);

    clearError();

    if (signupInvitations.length === 0) {
      setErrorMessage("Error: No Invitations.");
      return;
    }

    const isValid = validate();

    if (!isValid) {
      setErrorMessage("Error: Please fill in all the data.");
      return;
    }

    try {
      setIsBusy(true);

      await userService.createSignupInvitations(signupInvitations);

      setIsBusy(false);
    } catch (error: any) {
      setIsBusy(false);

      const errorMessage = "Error: " + error.response.data;
      setErrorMessage(errorMessage);
      return;
    }

    navigateAway();
  };

  const clearError = () => {
    setErrorMessage("");
  };

  const validate = () => {
    let isValid = true;

    for (const invitation of signupInvitations) {
      const isValidInvitation =
        invitation.organization !== undefined &&
        invitation.email.length > 0 &&
        invitation.email.indexOf("@") > 0 &&
        invitation.firstName.length > 0 &&
        invitation.lastName.length > 0;

      invitation.hasError = !isValidInvitation;

      if (!isValidInvitation) {
        isValid = false;
      }
    }

    updateInvitations();

    return isValid;
  };

  return (
    <BoxContent title="Invite Users" useFullWidth>
      <div className="input-form">
        <div className="mb-3 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onAddInvitation}
          >
            Add Invitation
          </button>
          <div>
            <input
              type="file"
              accept=".csv"
              id="uploadFileButton"
              hidden
              onChange={onFileUpload}
              ref={fileUploadRef}
            />
            <label className="btn btn-primary" htmlFor="uploadFileButton">
              Import Invitations
            </label>
            <button
              type="button"
              className="btn btn-primary ms-2"
              onClick={onDownloadTemplate}
            >
              Download Template
            </button>
          </div>
        </div>

        <table
          className="mb-3 table-spacing"
          // style={{ borderCollapse: "separate", borderSpacing: "10px 0px" }}
        >
          <thead>
            <tr>
              <th scope="col">Organization</th>
              <th scope="col">Email</th>
              <th scope="col">First Name</th>
              <th scope="col">Last Name</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {signupInvitations.map(
              (signupInvitation: SignupInvitation, index) => (
                <tr
                  key={index.toString()}
                  className={signupInvitation.hasError ? "table-row-error" : ""}
                >
                  <td style={{ width: "220px" }}>
                    <Select
                      options={organizationOptions}
                      value={getSelectedOrganization(signupInvitation)}
                      onChange={(option: any) => {
                        setSelectedOrganization(option, signupInvitation);
                      }}
                    />
                  </td>
                  <td style={{ width: "320px" }}>
                    <input
                      type="text"
                      className="form-control"
                      value={signupInvitation.email ?? ""}
                      onChange={(event) =>
                        onEmailChanged(event, signupInvitation)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={signupInvitation.firstName ?? ""}
                      onChange={(event) =>
                        onFirstNameChanged(event, signupInvitation)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={signupInvitation.lastName ?? ""}
                      onChange={(event) =>
                        onLastNameChanged(event, signupInvitation)
                      }
                    />
                  </td>

                  <td>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => onRemoveInvitation(signupInvitation)}
                    >
                      Remove Invitation
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>

        <div className="mb-3 d-flex align-items-center">
          <div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onInviteUsers}
              disabled={isBusy}
            >
              Invite Users
            </button>
          </div>

          {isBusy && <div className="ms-2 me-4">Please wait...</div>}

          <div>
            <button
              type="button"
              className="btn btn-secondary ms-2"
              onClick={onCancel}
              disabled={isBusy}
            >
              Cancel
            </button>
          </div>
        </div>
        <div className="col-md-12 text-center mt-3">
          {errorMessage !== "" && (
            <div className="error-message">{errorMessage}</div>
          )}
        </div>
      </div>
    </BoxContent>
  );
};

export default SignupInvitationsInvite;
